import { useEffect, useState } from 'react';
import type { BbgUsageResponse } from '@shared/contract';
import { api } from '@/api/client';

export interface BbgUsageState {
  data: BbgUsageResponse | null;
  error: string | null;
  loading: boolean;
  updatedAt: Date | null;
}

/** Matches the server's cache TTL — polling faster would only re-read the cache. */
const POLL_MS = 60_000;

/**
 * Bloomberg usage history, polled while the view is mounted. A failed refresh
 * keeps the last good data on screen and reports the error alongside it.
 */
export function useBbgUsage(days: number, pollMs = POLL_MS): BbgUsageState {
  const [state, setState] = useState<BbgUsageState>({
    data: null,
    error: null,
    loading: true,
    updatedAt: null,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.bbgUsage(days);
        if (!cancelled) setState({ data, error: null, loading: false, updatedAt: new Date() });
      } catch (err) {
        if (cancelled) return;
        const error = err instanceof Error ? err.message : String(err);
        setState((prev) => ({ ...prev, error, loading: false }));
      }
    };
    void load();
    const timer = setInterval(() => void load(), pollMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [days, pollMs]);

  return state;
}
